import { isValid } from "iban";
import { z } from "zod";
import {
  countries,
  currencies,
  defaultCountry,
  defaultCurrency,
  defaultDocumentLocale,
  defaultTimezone,
  documentLocales,
  invoicingTypes,
  payoutTypes,
  PayoutType,
  timezones,
} from "@evenli-platform/ui/lib/constants";
import { stringTrimmedNotEmptyNullable } from "@evenli-platform/ui/lib/utils-custom";
import { paths } from "@evenli-platform/core/clients/evenli/evenli";

export type Memberships =
  paths["/dashboard/v1/users/{userId}/memberships"]["get"]["responses"]["200"]["content"]["application/json"]["memberships"];

// Schema for SIGN IN form
export const signInSchema = z.object({
  email: z.string().email({ message: "enter_a_valid_email_address" }),
  password: z.string().min(8, "password_must_be_at_least_8_characters"),
});
export type SignInSchemaType = z.infer<typeof signInSchema>;

export const codeRequestFormSchema = z.object({
  email: z.string().email({ message: "enter_a_valid_email_address" }),
});
export type CodeRequestSchemaType = z.infer<typeof codeRequestFormSchema>;

export const AddressSchema = z.object({
  streetAddress: z.string().trim().min(2).max(70),
  postalCode: z.string().trim().min(2).max(10),
  city: z.string().trim().min(2).max(35),
  country: z.enum(countries),
});

export const newPasswordSchema = z
  .object({
    code: z.string().min(1, "this_field_is_required"),
    password: z
      .string()
      .min(8, "password_must_be_at_least_8_characters")
      .refine((value) => /[a-z]/.test(value), {
        message: "password_must_contain_lowercase",
      })
      .refine((value) => /[A-Z]/.test(value), {
        message: "password_must_contain_uppercase",
      })
      .refine((value) => /[0-9]/.test(value), {
        message: "password_must_contain_number",
      })
      .refine((value) => /[\W_]/.test(value), {
        message: "password_must_contain_special_character",
      }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "passwords_must_match",
    path: ["confirmPassword"],
  });
export type NewPasswordSchemaType = z.infer<typeof newPasswordSchema>;

// Schema for SIGN UP form
export const signUpSchema = z
  .object({
    email: z.string().email(),
    firstName: z.string().min(1, "this_field_is_required"),
    lastName: z.string().min(1, "this_field_is_required"),
    phoneNumber: z
      .string()
      .regex(/^\+\d*$/, "phone_number_must_be_valid")
      .min(10, "phone_number_is_too_short"),
    password: z
      .string()
      .min(8, "password_must_be_at_least_8_characters")
      .refine((value) => /[a-z]/.test(value), {
        message: "password_must_contain_lowercase",
      })
      .refine((value) => /[A-Z]/.test(value), {
        message: "password_must_contain_uppercase",
      })
      .refine((value) => /[0-9]/.test(value), {
        message: "password_must_contain_number",
      })
      .refine((value) => /[\W_]/.test(value), {
        message: "password_must_contain_special_character",
      }),
    confirmPassword: z.string(),
    terms: z.boolean(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "passwords_must_match",
    path: ["confirmPassword"],
  });

export type SignUpSchemaType = z.infer<typeof signUpSchema>;

export const dayServiceHoursSchema = z.object({
  dayOfWeek: z.number().min(0).max(6),
  isActive: z.boolean(),
  startTime: z.string().regex(/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/),
  endTime: z.string().regex(/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/),
});

export const serviceHoursFormSchema = z.array(dayServiceHoursSchema).refine(
  (data) => {
    return data.every((day) => {
      if (!day.isActive) return true;
      return day.startTime < day.endTime;
    });
  },
  {
    message: "start_time_must_be_before_end_time",
  },
);

export const orgDetailsSchema = z.object({
  name: z.string().min(1, "company_name_is_required"),
  businessId: z.string().min(1),
  country: z.enum(countries).default(defaultCountry),
  city: z.string().min(1, "city_is_required"),
  streetAddress: z.string().min(1, "this_field_is_required"),
  postalCode: z.string().min(5, "min_5_digits"),
  timezone: z.enum(timezones).default(defaultTimezone),
  documentLocale: z.enum(documentLocales).default(defaultDocumentLocale),
});

export type OrgDetailsSchemaType = z.infer<typeof orgDetailsSchema>;

// Schema for BILLING INFORMATION form
export const billingInfoSchema = z
  .object({
    isAnotherCompany: z.boolean(),
    billingBusinessName: z.string().nullable().optional(),
    billingBusinessId: z.string().nullable().optional(),
    billingEmail: z.string().email(),
    billingEinvoicingAddress: z.string().min(1),
    billingEinvoicingOperator: z.string().min(1),
    billingAdditionalInformation: z.string().optional(),
    billingIsVatExempt: z.boolean(),
    billingAddressLine: z.string().optional(),
    billingCity: z.string().optional(),
    billingPostalCode: z.string().optional(),
    billingCountry: z.enum(countries).optional(),
    billingBankAccountNumber: z
      .string()
      .refine((iban) => isValid(iban), { message: "invalid_iban" })
      .optional(),
    billingBic: z
      .string()
      .max(11, "bic_must_be_at_most_11_characters")
      .optional(),
    billingCurrency: z.enum(currencies).default(defaultCurrency),
    isAnotherPostalAddress: z.boolean(),
    netPaymentTerm: z.number().optional(),
    billingContactPersonName: z.string().optional(),
    billingBuyerReference: z.string().optional(),
    billingOrderIdentifier: z.string().optional(),
  })
  // .refine(
  //   (data) => {
  //     return !data.isAnotherCompany || !!data.billingBusinessName;
  //   },
  //   {
  //     message: "this_field_is_required",
  //     path: ["billingBusinessName"],
  //   },
  // )
  .refine(
    (data) => {
      return !data.isAnotherCompany || !!data.billingBusinessId;
    },
    {
      message: "this_field_is_required",
      path: ["billingBusinessId"],
    },
  )
  .refine(
    (data) => {
      return !data.isAnotherPostalAddress || !!data.billingAddressLine;
    },
    {
      message: "this_field_is_required",
      path: ["billingAddressLine"],
    },
  )
  .refine(
    (data) => {
      return !data.isAnotherPostalAddress || !!data.billingCity;
    },
    {
      message: "this_field_is_required",
      path: ["billingCity"],
    },
  )
  .refine(
    (data) => {
      return !data.isAnotherPostalAddress || !!data.billingCountry;
    },
    {
      message: "this_field_is_required",
      path: ["billingCountry"],
    },
  )
  .refine(
    (data) => {
      return !data.isAnotherPostalAddress || !!data.billingPostalCode;
    },
    {
      message: "this_field_is_required",
      path: ["billingPostalCode"],
    },
  );

export type BillingInfoSchemaType = z.infer<typeof billingInfoSchema>;

export const EditBillingSchema = z
  .object({
    businessId: z.string().trim().min(1).max(35),
    businessName: z.string().trim().min(1).max(70),
    emailAddress: z.string().trim().email().min(1),
    contactPersonName: stringTrimmedNotEmptyNullable().default(null),
    invoicingDeliveryType: z.enum(invoicingTypes),
    documentLocale: z.enum(documentLocales),
    einvoicingAddress: stringTrimmedNotEmptyNullable(35).default(null),
    einvoicingOperator: stringTrimmedNotEmptyNullable(35).default(null),
    netPaymentTerm: z.number().int().min(0),
    currency: z.enum(currencies).default(defaultCurrency),
    buyerReference: stringTrimmedNotEmptyNullable().default(null),
    orderIdentifier: stringTrimmedNotEmptyNullable().default(null),
    isVatExempt: z.boolean(),
    additionalInformation: stringTrimmedNotEmptyNullable().default(null),
    ...AddressSchema.shape,
  })
  .refine(
    (data) => {
      return (
        data.invoicingDeliveryType !== "einvoice" || !!data.einvoicingAddress
      );
    },
    {
      message: "this_field_is_required",
      path: ["einvoicingAddress"],
    },
  )
  .refine(
    (data) => {
      return (
        data.invoicingDeliveryType !== "einvoice" || !!data.einvoicingOperator
      );
    },
    {
      message: "this_field_is_required",
      path: ["einvoicingOperator"],
    },
  );

export type EditBillingInfoType = z.infer<typeof EditBillingSchema>;

// Updated site schema
export const siteChangeSchema = z.object({
  name: z.string().min(1, "this_field_is_required"),
  streetAddress: z.string().min(1, "this_field_is_required"),
  city: z.string().min(1, "city_is_required"),
  postalCode: z.string().min(1, "this_field_is_required"),
  country: z.enum(countries),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  public: z.boolean(),
  freeHeating: z.boolean(),
});

export const EditPayoutsSchema = z
  .object({
    businessName: stringTrimmedNotEmptyNullable().default(null),
    businessId: stringTrimmedNotEmptyNullable().default(null),
    streetAddress: z.string().trim().min(1),
    postalCode: z.string().trim().min(1),
    city: z.string().trim().min(1),
    country: z.enum(countries),
    bankAccountNumber: z
      .string()
      .refine((iban) => isValid(iban), { message: "invalid_iban" }),
    bic: z.string().trim().min(1).max(11, "bic_must_be_at_most_11_characters"),
    emailAddress: z.string().trim().email(),
    documentLocale: z.enum(documentLocales),
    personFirstName: stringTrimmedNotEmptyNullable().default(null),
    personLastName: stringTrimmedNotEmptyNullable().default(null),
    payoutType: z.enum(payoutTypes),
    referenceText: stringTrimmedNotEmptyNullable(70).default(null),
  })
  .refine(
    (data) => {
      return data.payoutType !== PayoutType.BUSINESS || !!data.businessName;
    },
    {
      message: "this_field_is_required",
      path: ["businessName"],
    },
  )
  .refine(
    (data) => {
      return data.payoutType !== PayoutType.BUSINESS || !!data.businessId;
    },
    {
      message: "this_field_is_required",
      path: ["businessId"],
    },
  )
  .refine(
    (data) => {
      return data.payoutType !== PayoutType.PERSON || !!data.personFirstName;
    },
    {
      message: "this_field_is_required",
      path: ["personFirstName"],
    },
  )
  .refine(
    (data) => {
      return data.payoutType !== PayoutType.PERSON || !!data.personLastName;
    },
    {
      message: "this_field_is_required",
      path: ["personLastName"],
    },
  );
export type EditPayoutsInfoType = z.infer<typeof EditPayoutsSchema>;

export enum Status {
  NotInitialized = "",
  InvitationNotFound = "Invitation not found",
  SomethingWentWrong = "Something went wrong",
  Authorized = "Authorized user",
  Unauthorized = "Unauthorized user",
}

export type socketInfo = {
  id: number;
  externalId: number;
  chargerId: number;
  status?: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  description?: string | null;
};
